import { Controller } from "@hotwired/stimulus"

// Naming someone in the composer. An @ and a few letters bring a small menu
// under the caret of the members whose handle or name begins that way, the
// ones the writer reads first (MentionsController); arrows or a tap choose,
// Enter or Tab writes the handle in, Escape puts the menu away. Nothing is
// looked up until there is an @ with a letter after it, and nothing is kept.
export default class extends Controller {
  static targets = [ "field", "menu", "item" ]
  static values = { url: String }

  connect() {
    this.onScroll = () => this.hide()
    window.addEventListener("scroll", this.onScroll, { passive: true })
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
    this.abort()
  }

  input() {
    const handle = this.handleAtCaret()
    if (!handle) { this.hide(); return }
    if (handle.text === this.asked) { this.place(); return }

    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.lookUp(handle.text), 120)
  }

  keydown(event) {
    if (this.menuTarget.hidden) return
    switch (event.key) {
      case "ArrowDown": event.preventDefault(); this.move(1); break
      case "ArrowUp": event.preventDefault(); this.move(-1); break
      case "Enter":
      case "Tab": {
        const item = this.selected()
        if (item) { event.preventDefault(); this.write(item.dataset.username) }
        break
      }
      case "Escape": event.preventDefault(); this.hide(); break
    }
  }

  // The menu is left a moment on blur, so a tap on a row lands before it goes.
  blur() {
    setTimeout(() => this.hide(), 150)
  }

  pick(event) {
    this.write(event.currentTarget.dataset.username)
  }

  // --- Looking up -----------------------------------------------------------

  async lookUp(text) {
    this.abort()
    this.controller = new AbortController()
    this.asked = text
    try {
      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(text)}`, {
        headers: { Accept: "text/html" }, credentials: "same-origin", signal: this.controller.signal
      })
      if (!response.ok) { this.hide(); return }
      // The field may have moved on while the answer was on its way.
      const now = this.handleAtCaret()
      if (!now || now.text !== text) return
      this.menuTarget.innerHTML = await response.text()
      this.place()
    } catch (error) {
      if (error.name !== "AbortError") this.hide()
    }
  }

  abort() {
    if (this.controller) this.controller.abort()
    this.controller = null
  }

  // The @handle being typed, ending at the caret: its text and where it starts.
  // An @ inside a word (an address, say) is not a name.
  handleAtCaret() {
    const field = this.fieldTarget
    if (field.selectionStart !== field.selectionEnd) return null
    const before = field.value.slice(0, field.selectionStart)
    const match = before.match(/(^|[^a-z0-9_@])@([a-z0-9_]{1,24})$/i)
    if (!match) return null
    return { text: match[2], start: before.length - match[2].length - 1 }
  }

  // --- The menu ---------------------------------------------------------------

  place() {
    const menu = this.menuTarget
    const field = this.fieldTarget
    const rect = field.getBoundingClientRect()
    const host = this.element.getBoundingClientRect()
    const caret = this.caretPosition(field, field.selectionEnd)
    menu.hidden = false
    const left = Math.min(Math.max(rect.left - host.left + caret.left - 12, 8), Math.max(host.width - menu.offsetWidth - 8, 8))
    menu.style.left = `${left}px`
    menu.style.top = `${rect.top - host.top + caret.top + caret.height + 6}px`
  }

  hide() {
    clearTimeout(this.timer)
    this.abort()
    this.asked = null
    if (this.hasMenuTarget) this.menuTarget.hidden = true
  }

  selected() {
    return this.itemTargets.find((item) => item.getAttribute("aria-selected") === "true") || this.itemTargets[0]
  }

  move(step) {
    const items = this.itemTargets
    if (items.length === 0) return
    const at = items.indexOf(this.selected())
    const next = (at + step + items.length) % items.length
    items.forEach((item, i) => item.setAttribute("aria-selected", i === next ? "true" : "false"))
  }

  // Writes the handle in place of the half-typed one, and a space after it,
  // through the field's own undo history where it exists.
  write(username) {
    const field = this.fieldTarget
    const handle = this.handleAtCaret()
    if (!handle) { this.hide(); return }
    const from = handle.start
    const to = field.selectionEnd
    const replacement = `@${username} `

    field.focus()
    field.setSelectionRange(from, to)
    if (!document.execCommand || !document.execCommand("insertText", false, replacement)) {
      field.setRangeText(replacement, from, to, "end")
    }
    field.dispatchEvent(new Event("input", { bubbles: true }))
    this.hide()
  }

  // Where a character is in the textarea, by laying the same text out in a
  // twin the reader never sees (as format_controller does).
  caretPosition(field, index) {
    const twin = document.createElement("div")
    const style = window.getComputedStyle(field)
    for (const name of [ "fontFamily", "fontSize", "fontWeight", "lineHeight", "letterSpacing", "padding", "border", "boxSizing", "whiteSpace", "wordWrap", "overflowWrap" ]) {
      twin.style[name] = style[name]
    }
    twin.style.position = "absolute"
    twin.style.visibility = "hidden"
    twin.style.whiteSpace = "pre-wrap"
    twin.style.width = `${field.clientWidth}px`
    twin.textContent = field.value.slice(0, index)
    const marker = document.createElement("span")
    marker.textContent = "\u200b"
    twin.append(marker)
    document.body.append(twin)
    const position = { left: marker.offsetLeft, top: marker.offsetTop - field.scrollTop, height: marker.offsetHeight || parseFloat(style.lineHeight) || 24 }
    twin.remove()
    return position
  }
}
